import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Input,
  Button,
  Badge,
  Row,
  Col,
  Label,

} from "reactstrap";
import { Link } from "react-router-dom";
import NewProyectoModal from "./NewProyectoModal";
import NewSubproyectoModal from "./NewSubproyectoModal";
import ConfirmProyectoRemovalModal from "./ConfirmProyectoRemovalModal";
import ViewSubproyectosModal from "./ViewSubproyectosModal";
import { IconEye } from "../ui/icons";
import axios from "axios";
import Moment from "moment";
import { API_URL_subproyectos } from "../../constants";
import { getAuthHeaders } from "../../utils/auth";

// ---------- Helpers ----------
const fmtDate = (d) => (d ? Moment(d).format("YYYY-MM-DD") : "—");
const safeStr = (v) => (v ?? "").toString();

// Normalize for searching: lower-case, trim, remove diacritics
const norm = (s) =>
  (s ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

// Build a flat string of all searchable fields for a proyecto
const makeProyectoSearchText = (p) => {
  const fields = [
    p?.proyecto_code,
    p?.name,
    p?.status,
    p?.cliente,
    p?.client_code,
    p?.producto,
    p?.tipo,
    p?.modalidad,
    p?.moneda,
    p?.pais,
    p?.ciudad,
    // Use the backend-provided human-readable fields
    p?.sede_name,
    p?.sede_subcategory_name,
    p?.sede_subcategory_code,
    // Include dates for searching
    p?.inicio_date,
    p?.terminacion_date,
    p?.registration_date,
  ];

  return norm(fields.filter(Boolean).join(" | "));
};

// ---------- Component ----------
const ProyectoList = ({ proyectos = [], itemsPerPage = 10, resetState }) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Debounced search input -> searchQuery
  const [rawInput, setRawInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [subproyectosMap, setSubproyectosMap] = useState({});
  const [selectedProyectoCode, setSelectedProyectoCode] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setSearchQuery(rawInput), 200);
    return () => clearTimeout(id);
  }, [rawInput]);

  // --- Search / Filter ---
  const tokens = useMemo(() => {
    const q = norm(searchQuery);
    return q.length ? q.split(/\s+/).filter(Boolean) : [];
  }, [searchQuery]);

  const filteredProyectos = useMemo(() => {
    if (!tokens.length) return proyectos || [];

    return (proyectos || []).filter((p) => {
      const searchText = makeProyectoSearchText(p);
      return tokens.every((t) => searchText.includes(t));
    });
  }, [proyectos, tokens]);

  // --- Pagination ---
  const safePerPage = Math.max(1, itemsPerPage || 10);
  const totalPages = Math.max(1, Math.ceil(filteredProyectos.length / safePerPage));
  const safePage = Math.min(currentPage, totalPages);
  const start = (safePage - 1) * safePerPage;
  const paginatedProyectos = filteredProyectos.slice(start, start + safePerPage);

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // --- Subproyectos modal handling ---
  const toggleModal = (proyectoCode) => {
    if (proyectoCode) {
      fetchSubproyectos(proyectoCode);
      setSelectedProyectoCode(proyectoCode);
    }
    setModalOpen((o) => !o);
  };

  const fetchSubproyectos = async (proyectoCode) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL_subproyectos}?proyecto=${proyectoCode}`, { headers });
      setSubproyectosMap((prev) => ({ ...prev, [proyectoCode]: res.data }));
    } catch (err) {
      console.error("Error fetching subproyectos", err);
    }
  };

  // --- Pagination helper ---
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const maxVisible = 7;
    let startPage = Math.max(1, safePage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    const pages = [];

    // First page
    if (startPage > 1) {
      pages.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        pages.push(
          <PaginationItem key="ellipsis1" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Visible pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem active={i === safePage} key={i}>
          <PaginationLink onClick={() => setCurrentPage(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <PaginationItem key="ellipsis2" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      pages.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => setCurrentPage(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination>
        <PaginationItem disabled={safePage === 1}>
          <PaginationLink previous onClick={() => setCurrentPage(safePage - 1)} />
        </PaginationItem>
        {pages}
        <PaginationItem disabled={safePage === totalPages}>
          <PaginationLink next onClick={() => setCurrentPage(safePage + 1)} />
        </PaginationItem>
      </Pagination>
    );
  };

  return (
    <div>
      {/* Search & Create */}
      <div className="mb-4">
        <Row className="g-3 align-items-end">
          <Col md={9} lg={10}>
            <Label htmlFor="proyecto-search" className="fw-bold mb-2">
              Buscar Proyectos
            </Label>
            <Input
              id="proyecto-search"
              type="text"
              placeholder="Buscar por nombre, código, cliente, value stream, subcategoría, país, ciudad..."
              value={rawInput}
              onChange={(e) => {
                setCurrentPage(1);
                setRawInput(e.target.value || "");
              }}
              style={{
                fontSize: '1.1rem',
                height: '50px',
                padding: '0.75rem 1rem',
                borderWidth: '1px',
              }}
              aria-label="Buscar proyectos"
            />
            {rawInput && (
              <small className="text-muted mt-1 d-block">
                Mostrando <strong>{filteredProyectos.length}</strong> de{" "}
                <strong>{proyectos.length}</strong> proyectos
              </small>
            )}
          </Col>
          <Col md={3} lg={2} className="text-end">
            <NewProyectoModal create resetState={resetState} />
          </Col>
        </Row>
  </div>

      {/* Table */}
      <Table striped responsive hover>
        <thead>
          <tr>
            <th>Código</th>
            <th>Nombre Proyecto</th>
            <th>Status</th>
            <th>Cliente</th>
            <th>Código Cliente</th>
            <th>Producto</th>
            <th>Tipo</th>
            <th>Modalidad</th>
            <th>Value Stream</th>
            <th>Subcategoría</th>
            <th>País</th>
            <th>Ciudad</th>
            <th>Inicio</th>
            <th>Terminación</th>
            <th>Moneda</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {paginatedProyectos.length > 0 ? (
            paginatedProyectos.map((p, idx) => {
              const code = safeStr(p.proyecto_code);
              return (
                <tr key={p.pk || code || idx}>
                  <td>
                    <code>{code || "—"}</code>
                  </td>
                  <td>
                    {code ? (
                      <Link to={`/detalle-proyectos/${code}`}>
                        <strong>{p.name || "—"}</strong>
                      </Link>
                    ) : (
                      <strong>{p.name || "—"}</strong>
                    )}
                  </td>
                  <td>
                    <Badge color={p.status === "Activo" ? "success" : "secondary"}>
                      {p.status || "—"}
                    </Badge>
                  </td>
                  <td>{p.cliente || "—"}</td>
                  <td>{p.client_code || "—"}</td>
                  <td>{p.producto || "—"}</td>
                  <td>{p.tipo || "—"}</td>
                  <td>
                    <small>{p.modalidad || "—"}</small>
                  </td>
                  
                  {/* Value Stream - from backend */}
                  <td>
                    {p.sede_name ? (
                      <>
                        {p.sede_name}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>

                  {/* Subcategoría - from backend */}
                  <td>
                    {p.sede_subcategory_name ? (
                      <>
                        {p.sede_subcategory_name}
                        {p.sede_subcategory_code && (
                          <>
                            {" "}
                            <Badge color="primary" pill>
                              {p.sede_subcategory_code}
                            </Badge>
                          </>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td>{p.pais || "—"}</td>
                  <td>{p.ciudad || "—"}</td>
                  <td>{fmtDate(p.inicio_date)}</td>
                  <td>{fmtDate(p.terminacion_date)}</td>
                  <td>
                    <Badge color="secondary">{p.moneda || "—"}</Badge>
                  </td>
                  <td className="lssi-action-col" style={{ whiteSpace: "nowrap" }}>
                    <Button
                      color="secondary"
                      size="sm"
                      className="me-2 mb-1"
                      onClick={() => code && toggleModal(code)}
                      disabled={!code}
                      title="Ver Subproyectos"
                      aria-label="Ver Subproyectos"
                      data-lssi-action-icon
                    >
                      <IconEye />
                      <span className="lssi-action-label"> Subproyectos</span>
                    </Button>

                    <NewSubproyectoModal
                      create
                      resetState={resetState}
                      proyectoCode={code}
                    />

                    <NewProyectoModal
                      create={false}
                      proyecto={p}
                      resetState={resetState}
                    />

                    <ConfirmProyectoRemovalModal pk={code} resetState={resetState} />
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="16" className="text-center py-4">
                {proyectos?.length
                  ? "No se encontraron proyectos con esos criterios."
                  : "Sin datos para mostrar."}
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <small className="text-muted">
            Página {safePage} de {totalPages} • Mostrando{" "}
            {Math.min(start + 1, filteredProyectos.length)} -{" "}
            {Math.min(start + safePerPage, filteredProyectos.length)} de{" "}
            {filteredProyectos.length} proyectos
          </small>
        </div>
        {renderPagination()}
      </div>

      {/* Subproyectos Modal */}
      {selectedProyectoCode && (
        <ViewSubproyectosModal
          isOpen={modalOpen}
          toggle={() => setModalOpen((o) => !o)}
          subproyectos={subproyectosMap[selectedProyectoCode] || []}
          proyectoCode={selectedProyectoCode}
        />
      )}
    </div>
  );
};

export default ProyectoList;